import { useState } from 'react';
import academicTermsApis from '../../../apis/academicTerms';
import { SnackbarSeverityType } from '../../../enums';
import { AcademicTerm } from '../types';

interface UseAcademicTermsDataProps {
  school: string;
  academicYear: string;
  selectedGrade: string;
  selectedDivision: string;
  onShowToast: (msg: string, severity: SnackbarSeverityType) => void;
}

export const useAcademicTermsData = ({
  school,
  academicYear,
  selectedGrade,
  selectedDivision,
  onShowToast
}: UseAcademicTermsDataProps) => {
  const [academicTerms, setAcademicTerms] = useState<AcademicTerm[]>([]);
  const [academicAllTerms, setAcademicAllTerms] = useState<AcademicTerm[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearched, setIsSearched] = useState(false);

  const fetchFilteredTerms = async () => {
    try {
      const response = await academicTermsApis.getAYTerms(school, {
        academicYear,
        grade: selectedGrade,
        division: selectedDivision
      });
      setAcademicTerms(Array.isArray(response) ? response : []);
    } catch (error: any) {
      console.error('Error fetching filtered academic terms:', error);
      setAcademicTerms([]);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to load academic terms.',
        SnackbarSeverityType.error
      );
    }
  };

  const fetchAllTerms = async () => {
    try {
      const response = await academicTermsApis.getAYTerms(school);
      setAcademicAllTerms(Array.isArray(response) ? response : []);
    } catch (error: any) {
      console.error('Error fetching all academic terms:', error);
      setAcademicAllTerms([]);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to load all academic terms.',
        SnackbarSeverityType.error
      );
    }
  };

  const handleSearch = async () => {
    setIsSubmitting(true);
    setIsSearched(false);

    await Promise.all([fetchFilteredTerms(), fetchAllTerms()]);

    setIsSubmitting(false);
    setIsSearched(true);
  };

  const previousAcademicTerms = academicTerms.map(({ id, orderSequence }) => ({
    termId: id,
    orderSequence
  }));

  const availableTermsOptions = academicAllTerms
    .filter((term) => !academicTerms.some((t) => t.id === term.id))
    .map(({ id, name }) => ({ label: name, value: id }));

  return {
    academicTerms,
    academicAllTerms,
    isSubmitting,
    isSearched,
    setAcademicTerms,
    handleSearch,
    setIsSearched,
    previousAcademicTerms,
    availableTermsOptions
  };
};
